/**
 * plan-rate-card — the pure half of the plan page's Rate Card panel (RP-6.6, RP-12.6,
 * RP-12.8; docs/specs/resource-plan.md).
 *
 * Three questions, answered without I/O so they can be tested exactly:
 *
 *   1. What should the plan header call the card it prices from?  (describePlanCard)
 *   2. If this plan moved to another Rate Basis, what would each line cost the client?
 *      (planRepricing / repricedRevenueCents)
 *   3. What has a Labor Category's rate been over time?            (sortRateHistory)
 *
 * PURE MODULE — no `@/db`, no `Bun`. The API and the UI both import it.
 *
 * Money is integer cents throughout, parsed with `parseMinor` — never `Number()` on a rate
 * (src/lib/rate-card.ts explains why).
 */

import { parseMinor } from "@/lib/rate-card";

// ── 1. The header's name for the card ─────────────────────────────────────────

/** The Account card actually in use on a plan, or null when no negotiated rate applies. */
export interface PlanAccountCardSummary {
  /** Labor Categories whose rate on this plan comes from the Account's negotiated card. */
  appliedCount: number;
  /** The latest start among those negotiated rates — the card "version" the plan is on. */
  effectiveFrom: string;
}

/**
 * "Acme card, effective 1 Jan 2026" — or "Standard Rate Card" when the Account has no
 * negotiated rate that applies to this plan.
 *
 * The header used to name the Account's card whenever the plan was SET to use it, which is
 * the default for every plan — so it claimed negotiated rates on plans priced entirely at
 * list. `accountCard` is what decides now: null means nothing negotiated applies.
 */
export function describePlanCard(input: {
  rateMode: string;
  clientAccountName: string;
  accountCard: PlanAccountCardSummary | null;
}): string {
  if (input.rateMode === "ClientDictated") return "Client-Dictated rates";
  if (input.accountCard === null) return "Standard Rate Card";
  return `${input.clientAccountName} card, effective ${formatDateOnly(input.accountCard.effectiveFrom)}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * An effective date is a CALENDAR DATE stored as UTC midnight, so it is read in UTC.
 * Formatting it in local time renders the previous day for every viewer west of UTC — the
 * same trap AccountRateCardCard documents. Hand-rolled rather than `toLocaleDateString` so
 * the server and every browser produce the same string.
 */
export function formatDateOnly(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ""} ${d.getUTCFullYear()}`;
}

/**
 * A negotiated rate against the list one, as "−$25.00 (−9.6%)" — the percentage of the
 * list rate, "how far off list is this deal" (RP-6.2). In minor units: these are money
 * strings, and subtracting them as Numbers would reintroduce float error.
 *
 * Null when there is nothing to compare, so the caller renders a dash, not a fake 0.
 */
export function formatRateDelta(
  listRate: string | null,
  negotiatedRate: string | null,
): { text: string; direction: "discount" | "premium" | "level" } | null {
  if (listRate === null || negotiatedRate === null) return null;
  const listCents = parseMinor(listRate, 2);
  const deltaCents = parseMinor(negotiatedRate, 2) - listCents;
  if (deltaCents === 0) return { text: "no change", direction: "level" };

  const sign = deltaCents < 0 ? "−" : "+";
  const abs = Math.abs(deltaCents);
  const money = `${sign}$${formatCents(abs)}`;
  // A 0.00 list rate has no meaningful percentage to quote — show money only.
  const pct = listCents === 0 ? "" : ` (${sign}${((abs / listCents) * 100).toFixed(1)}%)`;
  return { text: `${money}${pct}`, direction: deltaCents < 0 ? "discount" : "premium" };
}

// ── 2. Re-pricing a plan onto another Rate Basis ──────────────────────────────

export type LineRateSource = "Card" | "AccountOverride" | "LineOverride" | "ClientDictated";

export interface RepriceLineInput {
  id: string;
  laborCategoryId: string;
  billRate: string;
  rateSource: LineRateSource;
}

export interface RepriceChange {
  lineId: string;
  laborCategoryId: string;
  fromRate: string;
  fromSource: LineRateSource;
  toRate: string;
  toSource: "Card" | "AccountOverride";
}

export interface RepriceResult {
  /** Lines whose rate or source moves. */
  changes: RepriceChange[];
  /** Card / Account lines whose rate is already what the new basis says. */
  unchangedLineIds: string[];
  /** Custom-rate and Client-Dictated lines — they keep their rate by design (D9). */
  keptLineIds: string[];
  /** Labor Categories with no rate at all on the new basis — re-pricing must refuse. */
  missingLaborCategoryIds: string[];
}

/**
 * What re-pricing a plan onto a new Rate Basis does to each line.
 *
 * Only lines that FOLLOW a card move — those priced from the Rate Card or from the
 * Account's negotiated card. A line override was a deliberate concession and a
 * Client-Dictated rate is the contract itself; neither is a card rate, so neither is
 * re-priced. The new rate resolves exactly as RP-6.1 does for a new line: the Account's
 * negotiated rate on that basis if there is one, else the card.
 */
export function planRepricing(input: {
  lines: readonly RepriceLineInput[];
  /** Rate Card bill rates on the NEW basis, by Labor Category. */
  cardRates: ReadonlyMap<string, string>;
  /** The Account's negotiated rates on the NEW basis, by Labor Category. */
  overrideRates: ReadonlyMap<string, string>;
}): RepriceResult {
  const changes: RepriceChange[] = [];
  const unchangedLineIds: string[] = [];
  const keptLineIds: string[] = [];
  const missing = new Set<string>();

  for (const line of input.lines) {
    if (line.rateSource === "LineOverride" || line.rateSource === "ClientDictated") {
      keptLineIds.push(line.id);
      continue;
    }
    const override = input.overrideRates.get(line.laborCategoryId);
    const card = input.cardRates.get(line.laborCategoryId);
    const toRate = override ?? card;
    if (toRate === undefined) {
      missing.add(line.laborCategoryId);
      continue;
    }
    const toSource = override !== undefined ? "AccountOverride" : "Card";
    if (parseMinor(toRate, 2) === parseMinor(line.billRate, 2) && toSource === line.rateSource) {
      unchangedLineIds.push(line.id);
      continue;
    }
    changes.push({
      lineId: line.id,
      laborCategoryId: line.laborCategoryId,
      fromRate: line.billRate,
      fromSource: line.rateSource,
      toRate,
      toSource,
    });
  }

  return { changes, unchangedLineIds, keptLineIds, missingLaborCategoryIds: [...missing] };
}

/**
 * A line's revenue once its rate moves, in cents.
 *
 * Scales the line's own revenue by new ÷ old rate rather than re-deriving hours, because
 * the line's revenue already carries everything that multiplies the rate (ramp, steady
 * state, the plan window). Exact for every line with a non-zero rate; a $0.00 line has no
 * ratio to scale by, so it falls back to hours × rate.
 */
export function repricedRevenueCents(input: {
  revenue: string;
  hours: string;
  fromRate: string;
  toRate: string;
}): number {
  const fromCents = parseMinor(input.fromRate, 2);
  const toCents = parseMinor(input.toRate, 2);
  if (fromCents === 0) {
    // hours carry 2dp (centi-hours); cents × centi-hours / 100 = cents.
    return roundHalfUp(parseMinor(input.hours, 2) * toCents, 100);
  }
  return roundHalfUp(parseMinor(input.revenue, 2) * toCents, fromCents);
}

function roundHalfUp(numerator: number, denominator: number): number {
  const sign = numerator < 0 !== denominator < 0 ? -1 : 1;
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  return sign * Math.floor((2 * n + d) / (2 * d));
}

/** Cents → a 2dp dollar string. `123456` → `"1234.56"`. */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const digits = Math.abs(cents).toString().padStart(3, "0");
  return `${sign}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

// ── 3. Rate history ───────────────────────────────────────────────────────────

/**
 * One dated rate for a Labor Category. Bill rates only — cost has no history (there is
 * one Avg Internal Cost per Labor Category), and a bill rate is what [ACCT] prices with.
 */
export interface RateHistoryEntry {
  source: "Card" | "Account";
  /**
   * current — in force today. past — its window has closed. replaced — an earlier value
   * of a negotiated rate someone later edited. removed — a negotiated rate since deleted.
   * scheduled — starts in the future.
   */
  status: "current" | "past" | "replaced" | "removed" | "scheduled";
  billRate: string;
  effectiveStart: string;
  effectiveEnd: string | null;
  /** "every basis" for an all-bases negotiated rate; the basis name otherwise. */
  basisLabel: string;
  /** When an edit or removal retired this value; null for live rows. */
  changedAt: string | null;
  reason: string | null;
  sourceDocumentReference: string | null;
}

/** Status of a live (not replaced, not removed) dated row on `now`. */
export function liveStatus(
  effectiveStart: Date,
  effectiveEnd: Date | null,
  now: Date,
): "current" | "past" | "scheduled" {
  if (effectiveStart > now) return "scheduled";
  if (effectiveEnd !== null && effectiveEnd <= now) return "past";
  return "current";
}

/**
 * Newest first: by start date, then — for the same start — the live row ahead of the
 * values it replaced, most recently replaced first. That keeps an edited negotiated rate
 * reading top-down as "now $210, was $200, before that $190".
 */
export function sortRateHistory(entries: readonly RateHistoryEntry[]): RateHistoryEntry[] {
  const liveFirst = (e: RateHistoryEntry) =>
    e.status === "replaced" || e.status === "removed" ? 1 : 0;
  return [...entries].sort((a, b) => {
    if (a.effectiveStart !== b.effectiveStart) {
      return a.effectiveStart < b.effectiveStart ? 1 : -1;
    }
    if (liveFirst(a) !== liveFirst(b)) return liveFirst(a) - liveFirst(b);
    const ac = a.changedAt ?? "";
    const bc = b.changedAt ?? "";
    if (ac !== bc) return ac < bc ? 1 : -1;
    return a.source === b.source ? 0 : a.source === "Account" ? -1 : 1;
  });
}
